import analytics from './analytics';
import * as events from './analytics/events';
import type { PlatformStorage } from './PlatformStorage';

export type HashFunction = (seed: string) => () => number;

export type Bucket = { id: string };

export type ABTest = {
  /** Set `false` to disable old tests. */
  active: boolean;

  /** Defines what `getBucketID` returns for unassigned players. Defaults to `control`. */
  default: string;

  /** More than 2 buckets can be defined */
  buckets: Bucket[];

  /** Set `true` so the test is not assigned automatically and can be assigned manually instead. Defaults to `false`. */
  assignManually?: boolean;

  /** Set `true` to only assign new players (old players get assigned to the default bucket). Defaults to `false`. */
  newPlayersOnly?: boolean;

  /** Set `true` to exclude this test from the AB tag. Defaults to `false`. */
  excludeFromABTag?: boolean;
};

export type ABConfig = { [testId: string]: ABTest };

function includeInABTag(config: ABConfig | undefined, testId: string) {
  if (!config?.[testId]) {
    return false;
  }

  if (testId.startsWith('0000_placebo')) {
    return false;
  }

  const { active, excludeFromABTag } = config[testId];

  return active && !excludeFromABTag;
}

function validateConfig(config: ABConfig) {
  // AB tag validation
  const abTagLength = Object.keys(config)
    .filter((testId) => includeInABTag(config, testId))
    .map(
      (testId) =>
        // 4 symbols from the start of the test ID
        Math.min(4, testId.length) +
        // 2 separators per test: `-` between the id and bucket, `,` between tests
        // (off-by-one on the last test)
        2 +
        // buckets are recodrded as indexes, so take the string length of the highest digit
        Math.max(config[testId].buckets.length - 1, 0).toString().length,
    )
    .reduce(
      (a, b) => a + b,
      // fix the off-by-one for the last separator
      -1,
    );
  const abTagAllowedLength = 1024;

  if (abTagLength > abTagAllowedLength) {
    console.warn(
      `AB tag can become too long. Try to limit the number of tests to 140 to prevent this from happening`,
    );
  }
}

const defaultPlaceboTests: ABConfig = {
  '0000_placebo_2': {
    active: true,
    default: 'a',
    buckets: [{ id: 'a' }, { id: 'b' }],
  },

  '0000_placebo_3': {
    active: true,
    default: 'a',
    buckets: [{ id: 'a' }, { id: 'b' }, { id: 'c' }],
  },

  '0000_placebo_4': {
    active: true,
    default: 'a',
    buckets: [{ id: 'a' }, { id: 'b' }, { id: 'c' }, { id: 'd' }],
  },

  '0000_placebo_5': {
    active: true,
    default: 'a',
    buckets: [{ id: 'a' }, { id: 'b' }, { id: 'c' }, { id: 'd' }, { id: 'e' }],
  },
};

export class ABTests {
  config?: ABConfig;
  hash?: HashFunction;
  isNewPlayer: boolean;
  storageKey: string;
  storage: {
    tests: { [key: string]: string };
    userID: string;
  };
  tests?: { [key: string]: string };

  constructor(
    public userID: string,
    appName: string,
    private platformStorage: PlatformStorage,
    private getExternalABTag: () => string,
  ) {
    // playerID is available after platform.initializeAsync
    this.storageKey = appName + 'AB';
    this.isNewPlayer = platformStorage.entry.count === 1;

    // initial data for first time users
    this.storage = {
      tests: {},
      userID: this.userID,
    };
  }

  /**
   * Initialize A/B tests.
   * @param config tests configuration
   * @param hash generate a function used for random bucket assignment
   * @param storageKey optionally override the default localStorage key derived from `appName`.
   */
  initialize(config: ABConfig, hash: HashFunction, storageKey?: string): void {
    validateConfig(config);

    this.config = { ...defaultPlaceboTests, ...config };
    this.hash = hash;

    if (storageKey) {
      this.storageKey = storageKey;
    }

    this.load();
    this.initializeTests();
    this.save();
    this.applyUserProperties();
  }

  /**
   * Load test assignments from platform storage, falling back to local storage.
   */
  private load(): void {
    if (Object.keys(this.platformStorage.abTests || {}).length > 0) {
      this.storage.tests = { ...this.platformStorage.abTests };
      return;
    }

    try {
      const data = localStorage.getItem(this.storageKey);
      if (data) {
        this.storage = JSON.parse(data);

        const userID = this.storage.userID;
        if (userID === undefined) {
          this.storage.userID = this.userID;
        } else if (userID !== this.userID) {
          // player has changed!
          // it happens when testing different users locally
          // all the ab tests will be reassigned
          this.storage.tests = {};
          this.storage.userID = this.userID;
        }
      }
    } catch (e) {
      console.error('FAILED TO LOAD AB TESTS!', e);
    }
  }

  /**
   * Store test assignments to platform storage.
   */
  private save(): void {
    this.platformStorage.abTests = { ...this.storage.tests };
  }

  /**
   * Initialize all active tests and set assignments if necessary
   */
  private initializeTests(): void {
    this.tests = this.storage.tests;

    for (const testID in this.config) {
      const testConfig = this.config[testID];

      // Skip inactive tests
      if (!testConfig.active) continue;

      // Skip tests that are assigned manually
      if (testConfig.assignManually) continue;

      // Skip players with test already assigned
      if (this.tests[testID]) continue;

      // Skip old players if we want only new players
      if (!this.isNewPlayer && testConfig.newPlayersOnly) continue;

      const bucketID = this.getUserBucketID(testID, this.userID);
      const newPlayersOnly = testConfig.newPlayersOnly || false;
      this.assignTest(testID, bucketID, newPlayersOnly);
    }
  }

  /**
   * Assign a user in a bucket for a specific test
   */
  private assignTest(
    testID: string,
    bucketID: string,
    newPlayersOnly: boolean,
  ): void {
    this.tests[testID] = bucketID;

    events.trackABTestAssigned({
      test: testID,
      bucket: bucketID,
      newPlayersOnly,
    });
  }

  /**
   * Force bucket assignment for a specific test
   */
  assignTestManually(
    testID: string,
    forceBucketID?: string,
    forceAssign?: boolean,
  ): void {
    this.throwIfNotInitialized();

    const testConfig = this.config[testID];
    const validBucketIds = testConfig.buckets.map(({ id }) => id);

    if (
      forceBucketID !== undefined &&
      !validBucketIds.includes(forceBucketID)
    ) {
      throw new Error(
        `Invalid bucket id '${forceBucketID}' for test '${testID}'. ` +
          `Valid bucket ids are: [${validBucketIds.join(', ')}]`,
      );
    }

    // skip already assigned or non-active tests
    if (
      (this.tests[testID] && !forceAssign) ||
      !testConfig ||
      !testConfig.active
    ) {
      return;
    }

    this.assignTest(
      testID,
      forceBucketID || this.getUserBucketID(testID, this.userID),
      false,
    );

    this.save();
    this.applyUserProperties();
  }

  /**
   * Store test assignment to analytics properties
   */
  private applyUserProperties(): void {
    const internalABTag = this.getAbTag();
    const externalABTag = this.getExternalABTag();
    const props: { [key: string]: string } = {
      ABtag:
        internalABTag && externalABTag
          ? `${internalABTag},${externalABTag}`
          : internalABTag || externalABTag,
    };

    for (const testID in this.tests) {
      props['abTest_' + testID] = this.tests[testID];
    }

    analytics.setUserProperties(props, analytics.toDefault);
  }

  /**
   * Add player's A/B bucket assignments into `payload` as `sourceAB_*`-prefixed properties.
   * @param payload Analytics payload object
   */
  applyPayloadProperties(payload: { [key: string]: any }): {
    [key: string]: any;
  } {
    this.throwIfNotInitialized();

    for (const testID in this.tests) {
      const test = this.config[testID];
      if (test && test.active) {
        payload['sourceAB_' + testID] = this.tests[testID];
      }
    }
    return payload;
  }

  getListOfActiveTests(): (ABTest & { id: string })[] {
    this.throwIfNotInitialized();

    const activeTests: (ABTest & { id: string })[] = [];

    for (const testID in this.config) {
      const test = this.config[testID];
      if (test.active) {
        activeTests.push({ ...test, id: testID });
      }
    }

    return activeTests;
  }

  getBucketID(testID: string): string {
    this.throwIfNotInitialized();

    return this.tests[testID];
  }

  getUserBucketID(testID: string, userID: string): string {
    this.throwIfNotInitialized();

    const buckets = this.config[testID].buckets;
    const hash = this.hash(testID + userID);

    const roll = Math.min(
      buckets.length - 1,
      Math.floor(hash() * buckets.length),
    );
    return buckets[roll].id;
  }

  getBucketConfig(testID: string): Bucket | null {
    this.throwIfNotInitialized();

    const testConfig = this.config[testID];
    if (!testConfig) {
      console.error('Invalid AB test name: ' + testID);
      return null; // it means we use an AB test that has no config; or a typo in AB test name
    }

    let bucketID = this.tests[testID];
    if (!bucketID) {
      bucketID = testConfig.default || 'control';
    }

    const buckets = testConfig.buckets;
    for (let i = 0; i < buckets.length; i++) {
      if (buckets[i].id === bucketID) {
        return buckets[i];
      }
    }
    // Did not find bucket. Should not happen besides a bug in THIS file or in our config
    console.error('Invalid AB config for ' + testID + '/' + bucketID);
    return null;
  }

  getAbTag(): string {
    return (
      // an the AB tag contains active tests
      Object.keys(this.tests)
        .filter((testId) => includeInABTag(this.config, testId))

        // Sorted by test ID so it's consistent between users
        .sort()
        .map((testId) => this.getTestTag(testId))
        .join(',')
    );
  }

  private throwIfNotInitialized(): void {
    if (!this.config) {
      throw Error('Initialize ABTests before usage!');
    }
  }

  private getTestTag(testId: string) {
    const bucketId = this.getBucketID(testId);
    const bucketIndex = this.config[testId].buckets.findIndex(
      (bucket) => bucket.id === bucketId,
    );

    // A test's tag contains part of the test id for readability.
    return `${testId.substring(0, 4)}-${bucketIndex}`;
  }
}

export default ABTests;
